import { Injectable, NgZone } from '@angular/core';
import { CustomBaseStore } from '@city/const';
import { CityService } from '@city/feature';
import { YmapsService } from '@yandex-maps/feature';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';

/**
 * Сервис для работы с инстансом карты. Необходимо
 * провайдить в компонент в котором находится карта
 */
@Injectable()
export class YandexMapComponentService {
  map$ = new BehaviorSubject<ymaps.Map | null>(null);

  constructor(
    private ymapsService: YmapsService,
    private ngZone: NgZone,
    private cityService: CityService,
  ) {}

  create(
    parentElement: HTMLElement | string,
    state?: ymaps.IMapState,
    options?: ymaps.IMapOptions,
  ): Observable<ymaps.Map> {
    const mapState$ = state ? of(state) : this.getCityMapState();

    // Перед созданием карты необходимо убедиться, что нет старой
    this.destroy();

    // Если убрать делей, карта в блоках с относительной шириной вылетает за пределы родителя.
    // Похоже, что яндексовый скрипт, который считает ее ширину и задает ее в абсолютном виде
    // в пикселях успевает быстрее ангуляра и верстка едет.
    return combineLatest([mapState$.pipe(take(1)), this.ymapsService.ymaps$.pipe(take(1))]).pipe(
      map(([mapState, ymaps]) => {
        return new ymaps.Map(parentElement, mapState, options);
      }),
      tap((mapInstance: ymaps.Map) =>
        this.ngZone.run(() => {
          this.map$.next(mapInstance);
        }),
      ),
    );
  }

  isCreated(): boolean {
    return Boolean(this.map$.value);
  }

  addGeoObjects(geoObjects: ymaps.GeoObject[], isClustered: boolean, clusterOptions?: ymaps.IClustererOptions): void {
    this.removeAllGeoObjects();

    if (isClustered) {
      this.addClusteredGeoObjects(geoObjects, clusterOptions);
    } else {
      if (geoObjects?.length) {
        const mapInstance = this.map$.value;
        geoObjects.forEach((obj: ymaps.GeoObject) => {
          mapInstance.geoObjects.add(obj);
        });

        mapInstance.setBounds(mapInstance.geoObjects.getBounds(), {
          checkZoomRange: true,
        });
      }
    }
  }

  removeAllGeoObjects(): void {
    this.map$.value.geoObjects.removeAll();
  }

  private addClusteredGeoObjects(geoObjects?: ymaps.GeoObject[], clusterOptions?: ymaps.IClustererOptions): void {
    if (geoObjects?.length) {
      const mapInstance = this.map$.value;
      const clusterer = new ymaps.Clusterer({
        preset: 'islands#invertedYellowClusterIcons',
        groupByCoordinates: false,
        ...(clusterOptions || {}),
      });

      clusterer.add(geoObjects);
      mapInstance.geoObjects.add(clusterer);

      mapInstance.setBounds(clusterer.getBounds(), {
        checkZoomRange: true,
      });
    }
  }

  private getCityMapState(): Observable<ymaps.IMapState> {
    return this.cityService.activeBaseStore$.pipe(
      map(({ cityMapZoom, cityGeoPoint }: CustomBaseStore) => {
        return {
          center: [cityGeoPoint.latitude, cityGeoPoint.longitude],
          zoom: cityMapZoom,
          controls: ['zoomControl', 'geolocationControl'],
        };
      }),
    );
  }

  private destroy(): void {
    if (this.map$.value) {
      this.map$.value.destroy();
      this.map$.next(null);
    }
  }
}
